/**
 * The user's profile, fetched once per session and shared.
 *
 * Several pages consult profile switches (whether to show the sponsorship
 * layer, what work authorization the user has). Each fetching /profile on
 * mount would be a request per page view for a document that changes when
 * the user edits it and at no other time. The Profile page resets this after
 * a save, so a change is visible on the next page immediately.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let cached = null;
let inflight = null;

export function resetProfileCache() {
  cached = null;
  inflight = null;
}

export function fetchProfile() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = Promise.resolve()
      .then(() => api.get("/profile"))
      .then((res) => {
        cached = (res && res.data) || {};
        return cached;
      })
      .catch(() => ({}))
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** The profile, or null until it has loaded. `{}` when it cannot be read. */
export function useProfile() {
  const [profile, setProfile] = useState(cached);
  useEffect(() => {
    let live = true;
    fetchProfile().then((p) => live && setProfile(p));
    return () => {
      live = false;
    };
  }, []);
  return profile;
}
